import { useMutation } from '@tanstack/react-query';

import { useLanguageDetector } from '@/hooks/pure/useLanguageDetector';

import type { TargetLanguage } from '../types';
import {
  isTranslatorSupported,
  translateWordCloud,
  type TranslateWordCloudResult,
} from './translateWordCloud';

export interface TranslateWordCloudMutationParams {
  keywords: string[];
  targetLanguage: TargetLanguage;
}

export interface UseTranslateWordCloudMutationResult {
  /** Whether both Translator and LanguageDetector APIs are supported */
  isSupported: boolean;
  /** Whether the language detector is available */
  isDetectorAvailable: boolean;
  /** Whether the language detector is loading */
  isDetectorLoading: boolean;
  /** Mutation function */
  mutate: (params: TranslateWordCloudMutationParams) => void;
  /** Async mutation function */
  mutateAsync: (
    params: TranslateWordCloudMutationParams,
  ) => Promise<TranslateWordCloudResult>;
  /** Whether translation is in progress */
  isPending: boolean;
  /** Translation result data */
  data: TranslateWordCloudResult | undefined;
  /** Translation error */
  error: Error | null;
  /** Reset mutation state */
  reset: () => void;
  /** Detector error */
  detectorError: Error | null;
}

/**
 * React Query mutation hook for translating word cloud keywords
 */
export const useTranslateWordCloudMutation =
  (): UseTranslateWordCloudMutationResult => {
    const {
      detectMostLikely,
      isSupported: isDetectorSupported,
      isAvailable: isDetectorAvailable,
      isLoading: isDetectorLoading,
      error: detectorError,
    } = useLanguageDetector({ enabled: true });

    const isSupported = isTranslatorSupported() && isDetectorSupported;

    const mutation = useMutation<
      TranslateWordCloudResult,
      Error,
      TranslateWordCloudMutationParams
    >({
      mutationFn: async ({ keywords, targetLanguage }) => {
        if (!isSupported) {
          throw new Error('Translation API not supported');
        }

        if (!isDetectorAvailable) {
          throw new Error('Language detector not available');
        }

        return translateWordCloud({
          keywords,
          targetLanguage,
          detectLanguage: detectMostLikely,
        });
      },
    });

    return {
      isSupported,
      isDetectorAvailable,
      isDetectorLoading,
      mutate: mutation.mutate,
      mutateAsync: mutation.mutateAsync,
      isPending: mutation.isPending,
      data: mutation.data,
      error: mutation.error,
      reset: mutation.reset,
      detectorError,
    };
  };
